import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import type { AllActiveSession } from '@/app/[locale]/agent/dashboard/dashboard.interface'

// Default filters configuration
export const DEFAULT_ALL_SESSION_FILTERS = {
  status: 'all', // 'all' | 'pending' | 'accepted' | 'completed' (maps to handover PENDING/ASSIGNED/COMPLETED statuses)
  platform: 'all',
  dateRange: 'all', // 'all' | 'last-hour' | 'last-4-hours' | 'today'
  messageCount: 'all', // 'all' | 'low' | 'medium' | 'high'
  priority: 'all',
  team: 'all',
  search: '',
}

export interface AllSessionsTeamFilterState {
  showAllTeams: boolean
  selectedTeamId: number | null
}

export interface UseAllSessionsFilteringParams {
  sessions: AllActiveSession[]
  user: { id?: number | string; teamIds?: number[] } | null
  isSupervisor: boolean
}

export interface UseAllSessionsFilteringResult {
  filters: typeof DEFAULT_ALL_SESSION_FILTERS
  setFilters: Dispatch<SetStateAction<typeof DEFAULT_ALL_SESSION_FILTERS>>
  allSessionsTeamFilter: AllSessionsTeamFilterState
  handleAllSessionsTeamFilterChange: (teamId: number | null) => void
  filteredSessions: AllActiveSession[]
  activeFilterCount: number
  resetFilters: () => void
}

export function useAllSessionsFiltering(
  params: UseAllSessionsFilteringParams
): UseAllSessionsFilteringResult {
  const { sessions, user } = params

  const [filters, setFilters] = useState({ ...DEFAULT_ALL_SESSION_FILTERS })
  const [allSessionsTeamFilter, setAllSessionsTeamFilter] =
    useState<AllSessionsTeamFilterState>({
      showAllTeams: true,
      selectedTeamId: null,
    })
  const handleAllSessionsTeamFilterChange = useCallback(
    (teamId: number | null) => {
      setAllSessionsTeamFilter({
        showAllTeams: teamId == null,
        selectedTeamId: teamId,
      })
    },
    []
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.platform !== 'all') count += 1
    if (filters.status !== 'all') count += 1
    if (filters.dateRange !== 'all') count += 1
    if (filters.messageCount !== 'all') count += 1
    if (filters.priority !== 'all') count += 1
    if (!allSessionsTeamFilter.showAllTeams) count += 1
    return count
  }, [filters, allSessionsTeamFilter.showAllTeams])

  const applyFilters = useCallback(
    (data: AllActiveSession[]) => {
      const filtered = data.filter(session => {
        // Get session's calculated team from backend (this is the authoritative team)
        const sessionTeamId = session.teamId

        // Team filtering
        if (allSessionsTeamFilter.selectedTeamId != null) {
          // Filtering by specific team - use the backend-calculated teamId
          if (sessionTeamId !== allSessionsTeamFilter.selectedTeamId) {
            return false
          }
        }
        // Note: When showAllTeams is true, backend already filtered sessions by agent's teams
        // So we don't need to re-filter here

        if (filters.platform !== 'all') {
          const normalizedPlatform = String(session.platform).toLowerCase()
          const selectedPlatform = String(filters.platform).toLowerCase()
          const PLATFORM_ALIASES: Record<string, string> = {
            facebook_messenger: 'facebook',
            whatsapp_business: 'whatsapp',
            telegram_bot: 'telegram',
            telegrambot: 'telegram',
          }
          const platformAlias =
            PLATFORM_ALIASES[normalizedPlatform] || normalizedPlatform
          if (platformAlias !== selectedPlatform) {
            return false
          }
        }

        if (filters.status !== 'all') {
          // Map frontend status filter to backend handover status
          // 'pending' -> PENDING, 'accepted' -> ASSIGNED, 'completed' -> COMPLETED
          const handoverStatus = session.lastAssignment?.status

          if (filters.status === 'pending' && handoverStatus !== 'PENDING')
            return false
          if (filters.status === 'accepted' && handoverStatus !== 'ASSIGNED')
            return false
          if (filters.status === 'completed' && handoverStatus !== 'COMPLETED')
            return false
        }

        if (filters.dateRange !== 'all') {
          const sessionDate = new Date(session.createdAt)
          const now = new Date()
          const diffHours =
            (now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60)
          switch (filters.dateRange) {
            case 'last-hour':
              if (diffHours > 1) return false
              break
            case 'last-4-hours':
              if (diffHours > 4) return false
              break
            case 'today':
              if (diffHours > 24) return false
              break
          }
        }

        if (filters.messageCount !== 'all') {
          switch (filters.messageCount) {
            case 'low':
              if (session.messageCount > 5) return false
              break
            case 'medium':
              if (session.messageCount <= 5 || session.messageCount > 20)
                return false
              break
            case 'high':
              if (session.messageCount <= 20) return false
              break
          }
        }

        if (filters.priority !== 'all') {
          const priority = session.pendingHandoverRequest?.priority
          if (!priority || priority !== filters.priority) {
            return false
          }
        }

        return true
      })

      return filtered
    },
    [filters, allSessionsTeamFilter.selectedTeamId]
  )

  const filteredSessions = useMemo(
    () => applyFilters(sessions),
    [sessions, applyFilters]
  )

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_ALL_SESSION_FILTERS })
    setAllSessionsTeamFilter({ showAllTeams: true, selectedTeamId: null })
  }, [])

  return {
    filters,
    setFilters,
    allSessionsTeamFilter,
    handleAllSessionsTeamFilterChange,
    filteredSessions,
    activeFilterCount,
    resetFilters,
  }
}
